import React from 'react';

function TextInput({
    label,
    id,
    name,
    type = "text",
    value,
    onChange,
    required = false,
    placeholder,
    iconClass, // For icons like the calendar or email
    ariaLabel,
    error,
    wrapperClassName = "mb-3", // New: Allows custom styling for the wrapper, defaults to Bootstrap margin
    inputClassName = "", // New: Allows custom styling for the input element itself
    onIconClick, // New: Callback for when the icon is clicked
    iconAriaLabel, // New: ARIA label for the icon if it's interactive
    labelClassName = "", // New: Allows custom styling for the label
    min, // New: For number inputs
    max, // New: For number inputs
    noWrapper = false, // New: If true, renders input and error without any wrapper div
    disabled = false, // New: To disable the input
    rows, // New: For textarea
}) {
    const inputId = id || name; // Ensure an id for htmlFor and aria-describedby

    let inputElement;
    if (type === "textarea") {
        inputElement = (
            <textarea
                className={`form-control ${inputClassName} ${error ? 'is-invalid' : ''}`.trim().replace(/\s+/g, ' ')}
                id={inputId}
                name={name}
                value={value}
                onChange={onChange}
                required={required}
                placeholder={placeholder}
                aria-label={ariaLabel || label}
                aria-describedby={error ? `${inputId}-error` : undefined}
                aria-invalid={!!error}
                rows={rows || 3} // Default to 3 rows if not specified
                disabled={disabled}
            />
        );
    } else {
        inputElement = (
            <input
                type={type}
                className={`form-control ${inputClassName} ${error ? 'is-invalid' : ''}`.trim().replace(/\s+/g, ' ')}
                id={inputId}
                name={name}
                value={value}
                onChange={onChange}
                required={required}
                placeholder={placeholder}
                aria-label={ariaLabel || label}
                aria-describedby={error ? `${inputId}-error` : undefined}
                aria-invalid={!!error}
                min={type === 'number' ? min : undefined}
                max={type === 'number' ? max : undefined}
                disabled={disabled} // Apply the disabled prop
                autoComplete={type === 'email' ? 'email' : type === 'password' ? 'current-password' : undefined} // Basic autocomplete for email and password
                autoFocus={type === 'text' && !label} // Auto-focus if it's a text input without a label
            />
        );
    }

    const iconSpan = iconClass && (
        onIconClick ? (
            <button
                type="button"
                className="input-group-text"
                onClick={onIconClick}
                aria-label={iconAriaLabel || 'Toggle visibility'} // Default ARIA label
                style={{ cursor: 'pointer' }} // Ensure pointer cursor for button-like behavior
            >
                <i className={iconClass}></i>
            </button>
        ) : (
            <span className="input-group-text" aria-hidden="true">
                <i className={iconClass}></i>
            </span>
        )
    );

    // If noWrapper is true, render only the input and its error message.
    // This mode bypasses labels, icons, and the main wrapper div.
    if (noWrapper) {
        return (
            <>
                {inputElement}
                {error && <div id={`${inputId}-error`} className="invalid-feedback" style={{ display: 'block' }}>{error}</div>}
            </>
        );
    }
    // If no label is provided AND an iconClass is present,
    // the input-group itself becomes the main container, merging with wrapperClassName.
    // This matches the structure in LoginScreen.jsx where input-group has the margin.
    if (!label && iconClass) {
        return (
            <div className={`input-group ${error ? 'has-validation' : ''} ${wrapperClassName}`}>
                {inputElement}
                {iconSpan}
                {error && <div id={`${inputId}-error`} className="invalid-feedback">{error}</div>}
            </div>
        );
    }

    // Default structure: an outer wrapper for the label and input/input-group.
    // This handles cases with a label, or no icon, or both.
    return (
        <div className={wrapperClassName}> {/* This div gets classes like mb-3 */}
            {label && <label htmlFor={inputId} className={`form-label ${labelClassName || ''}`.trim()}>{label}</label>}
            {iconClass ? ( // Label is present (due to above 'if' condition) OR iconClass is present
                <div className={`input-group ${error ? 'has-validation' : ''}`}>
                    {inputElement}
                    {iconSpan}
                    {error && <div id={`${inputId}-error`} className="invalid-feedback">{error}</div>}
                </div>
            ) : (
                // No iconClass: standalone input
                <>
                    {inputElement}
                    {error && <div id={`${inputId}-error`} className="invalid-feedback" style={{ display: 'block' }}>{error}</div>}
                </>
            )}
        </div>
    );
}
export default TextInput;